import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
    ɵFormGroupRawValue,
    ɵTypedOrUntyped,
} from '@angular/forms';
import {ErrorStateMatcher, MAT_DATE_FORMATS} from '@angular/material/core';
import {MatSnackBarRef} from '@angular/material/snack-bar';
import {ActivatedRoute} from '@angular/router';
import {
    AccountCardRequestV2,
    AccountRequestV2,
    AccountStatus,
    IncidentType,
    Participant,
    ParticipantRequestV2,
    PaymentPointRequestV2,
    ReferenceBookResponse,
    RequisiteType,
    SubjectType,
    TransactionType,
    WalletRequestV2,
} from '@npck/af-frontend-api';
import {DATE_SERVER_FORMAT, IIN_PATTERN, Spinner} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {filter, merge, Subscription} from 'rxjs';
import {DateForm} from '../../../../../../components/date-picker-field/date-picker-field.component';
import {DictionarySelectorComponent} from '../../../../../../components/dictionary/dictionary-selector.component';
import {getFormattedDateOrNull} from '../../../../../../components/full-table/filter/filter.component';
import {AuthHolderService} from '../../../../../../domain/auth-holder.service';
import {
    CARD_NUMBER_MAX_LENGTH,
    FOREIGN_IBAN_MIN_LENGTH,
    FOREIGN_IBAN_PATTERN,
    IBAN_MAX_LENGTH,
    KZ_IBAN_LENGTH,
    KZ_IBAN_PATTERN,
    MAX_PAYMENT_POINT_NUMBER,
    NUMBER_PATTERN,
    PATTERN_PAYMENT_POINT_NUMBER,
    PAYMENT_POINT_MAX_LENGTH,
    WALLET_NUMBER_MAX_LENGTH,
    WALLET_NUMBER_MIN_LENGTH,
} from '../../../../../../domain/constants';
import {DictionaryHolderService} from '../../../../../../domain/dictionary-holder.service';
import {getValidPhoneNumberOrNull, normalizePhonePrefix, PhoneInfo} from '../../../../../../domain/phone-utils';
import {INCIDENT_TYPE_CODE} from '../../../../../../domain/query-params.keys';
import {cardNumberValidator} from '../../../../../../infra/card-number.validator';
import {DATE_FORMATS} from '../../../../../../infra/date-time.adapter';

export const REQUISITE_ISSUER_BIN_CONTROL: Record<string, keyof ParticipantForm> = {
    [RequisiteType.ACCOUNT_KZ]: 'accountIssuerBin',
    [RequisiteType.CARD_MASK]: 'cardIssuerBin',
    [RequisiteType.ELECTRONIC_WALLET]: 'walletIssuerBin',
    [RequisiteType.PAYMENT_POINT]: 'paymentPointIssuerBin',
    [RequisiteType.ACCOUNT_FOREIGN_BANK]: 'foreignIssuerBin',
};
export const NO_EXTRA_REQUISITE_BLOCKS: RequisiteType[] = [];

const NOT_MY_CLIENT_ALWAYS_REQUIRED_CONTROLS: string[] = ['role', 'subjectType'];

@Component({
    selector: 'af-transaction-participant',
    templateUrl: './transaction-participant.component.html',
    styleUrl: './transaction-participant.component.scss',
    providers: [
        {provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS},
    ],
    standalone: false,
})
export class TransactionParticipantComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() label: string;
    @Input() form: FormGroup<ParticipantForm>;
    @Input() participant: Participant | ParticipantViewModel;
    @Input() mode: 'incident' | 'refund' = 'incident';
    @Input() creditRole: 'sender' | 'receiver' | null = null;
    @Input() paymentRole: 'sender' | 'receiver' | null = null;
    @Input() paymentOrganizationInvolved: boolean = false;
    @Input() lockedSubjectType: SubjectType | null = null;
    @Input() prefillRequisiteType: RequisiteType | null = null;
    @Input() phoneRequired: boolean = false;
    @Input() transferTypeSelected: boolean = false;
    @Input() cashTypeSelected: boolean = false;
    @Input() cashRole: 'withdrawal-sender' | 'top-up-receiver' | null = null;
    @Input() transactionType: string | null = null;
    @Input() excludedRequisiteTypes: string[] = [];
    @Input() extraRequisiteBlocks: RequisiteType[] = NO_EXTRA_REQUISITE_BLOCKS;
    @Input() borrowerCategoryControl?: FormControl<string>;
    @Input() participantSide: 'sender' | 'receiver' = 'sender';
    @Input() transactionStatus: string | null = null;
    @ViewChild('accountIssuerBinSelector') accountIssuerBinSelector!: DictionarySelectorComponent;
    @ViewChild('cardBinIssuerBinSelector') cardBinIssuerBinSelector!: DictionarySelectorComponent;
    @ViewChild('paymentPointBlock') paymentPointBlockTemplate!: TemplateRef<unknown>;
    @ViewChildren(DictionarySelectorComponent) dictionarySelectors!: QueryList<DictionarySelectorComponent>;
    Spinner = Spinner;
    Validators = Validators;
    DATE_FORMATS = DATE_FORMATS;
    IBAN_MAX_LENGTH = IBAN_MAX_LENGTH;
    KZ_IBAN_LENGTH = KZ_IBAN_LENGTH;
    maxDate = new Date();
    binDictionary: ReferenceBookResponse[] = [];
    cardBinDictionary: ReferenceBookResponse[] = [];
    showPhoneComponent: boolean = true;
    collapsed = false;
    isOtherIncidentType: boolean = false;
    isCompromisedPaymentInstrumentType: boolean = false;
    excludeRoleType: string[] = [];
    readonly organizationTypes = [
        {code: 'BANK', name_ru: 'БАНК'},
        {code: 'MFO', name_ru: 'МФО'},
        {code: 'PAYM', name_ru: 'ПО'},
    ];
    readonly requisiteConflictErrorStateMatcher: ErrorStateMatcher = {
        isErrorState: (control, formDirective) =>
            (!!control && control.invalid && (control.touched || !!formDirective?.submitted))
            || !!this.form?.hasError('requisiteOrganizationConflict'),
    };

    protected readonly SubjectType = SubjectType;
    private accountIssuerBinSubscription?: Subscription;
    private accountRequisiteRefreshSubscription?: Subscription;
    private cashRequisiteSubscription?: Subscription;
    private isRefreshingAccountFields = false;
    private notMyClientAvailabilitySubscription?: Subscription;
    private notMyClientSubscription?: Subscription;
    private paymInstrTypeSubscription?: Subscription;
    private paymentPointSubscription?: Subscription;
    private requisiteTypeSubscription?: Subscription;
    private requisiteLockSubscription?: Subscription;
    private requisiteConflictSubscription?: Subscription;
    private requisiteConflictSnackbarRef?: MatSnackBarRef<unknown>;
    private hadRequisiteConflict = false;
    private cardIssuerBinFromIban = false;
    private requisiteTypeModeLocked = false;
    private roleSubscription: Subscription;
    private subjectTypeSubscription: Subscription;
    private readonly fullCardNumberValidator: ValidatorFn = cardNumberValidator();
    private readonly requisiteOrganizationUnityValidator: ValidatorFn = this.buildRequisiteOrganizationUnityValidator();

    constructor(
        private cdr: ChangeDetectorRef,
        private dictionaryHolderService: DictionaryHolderService,
        private formBuilder: FormBuilder,
        private authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
        private snackbarService: SnackbarService,
    ) {
    }

    get showNotMyClient(): boolean {
        return (!!this.paymentRole || this.transferTypeSelected || !!this.cashRole)
            && !!this.form.controls.notMyClient
            && this.form.controls.notMyClient.enabled
            && this.authHolderService.hasAccess(['finance-user']);
    }

    get notMyClientLabelKey(): string {
        return this.participantSide === 'sender' ? 'i18n_af_labelSenderNotMyClient' : 'i18n_af_labelReceiverNotMyClient';
    }

    get hasRequisiteConflict(): boolean {
        return !!this.form?.hasError('requisiteOrganizationConflict');
    }

    get organizationLabelKey(): string {
        return this.participantSide === 'sender' ? 'i18n_af_labelSenderOrganization' : 'i18n_af_labelReceiverOrganization';
    }

    get showWalletIbanField(): boolean {
        return this.cashRole === 'top-up-receiver' && this.transactionType === TransactionType.TOPEWT;
    }

    private get isNotMyClient(): boolean {
        return !!this.form.controls.notMyClient?.value;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.paymentRole && !changes.paymentRole.isFirstChange()) {
            if (this.paymentRole) {
                this.showPhoneComponent = true;
            } else {
                if (this.isNotMyClient) {
                    this.form.controls.notMyClient.setValue(false);
                }
            }
        }
        if (changes.lockedSubjectType && !changes.lockedSubjectType.isFirstChange() && !this.lockedSubjectType) {
            this.form.get('subjectType')?.enable({emitEvent: false});
        }
        if (changes.phoneRequired && !changes.phoneRequired.isFirstChange()) {
            if (this.phoneRequired) {
                this.showPhoneComponent = true;
            }
            const role = this.form.get('role')?.value;
            if (role) {
                this.handleRoleChange(role);
            }
        }
        if (changes.transferTypeSelected && !changes.transferTypeSelected.isFirstChange()) {
            if (!this.transferTypeSelected && !this.paymentRole && this.isNotMyClient) {
                this.form.controls.notMyClient.setValue(false);
            }
            this.applyRequisiteOrganizationTypeRequired();
            this.refreshRequiredState();
        }
        if (changes.paymentOrganizationInvolved && !changes.paymentOrganizationInvolved.isFirstChange()) {
            const role = this.form.get('role')?.value;
            if (role) {
                this.handleRoleChange(role);
            }
        }
        if (changes.excludedRequisiteTypes && !changes.excludedRequisiteTypes.isFirstChange()) {
            const requisiteType = this.form.controls.requisiteType?.value;
            if (requisiteType && this.excludedRequisiteTypes.includes(requisiteType)) {
                this.form.controls.requisiteType.setValue(null);
            }
        }
        if (changes.creditRole && !changes.creditRole.isFirstChange()) {
            if (this.form.controls.subjectType) {
                Object.keys(this.form.controls).forEach(name =>
                    this.form.removeControl(name as keyof ParticipantForm, {emitEvent: false}));
                this.buildForm();
                this.cdr.detectChanges();
            } else if (this.creditRole === 'sender') {
                this.buildForm();
                this.cdr.detectChanges();
            }
        }
        if (changes.transactionStatus && !changes.transactionStatus.isFirstChange() && this.form?.controls?.iban) {
            this.refreshAccountFieldsState();
        }
        if (changes.extraRequisiteBlocks && !changes.extraRequisiteBlocks.isFirstChange() && this.form?.controls?.paymentPointNumber) {
            this.paymentPointDependentValidation();
        }
        if (changes.cashRole && !changes.cashRole.isFirstChange()) {
            if (!this.cashRole && !this.paymentRole && !this.transferTypeSelected && this.isNotMyClient) {
                this.form.controls.notMyClient.setValue(false);
            }
            this.refreshRequiredState();
        }
        if (changes.transactionType && !changes.transactionType.isFirstChange()) {
            this.refreshRequiredState();
        }
    }

    ngOnInit(): void {
        this.isOtherIncidentType = this.isIncidentType(IncidentType.OTHER);
        this.isCompromisedPaymentInstrumentType = this.isIncidentType(IncidentType.COMPROMISED_PAYMENT_INSTRUMENT);
        this.excludeRoleType = this.isCompromisedPaymentInstrumentType ? ['victim'] : [];
        if (this.participant) {
            this.form.addControl('id', new FormControl(this.participant.id));
            this.form.addControl('phoneNumberId', new FormControl(this.participant.phone_number?.id));
            this.form.addControl('cardMaskNumberId', new FormControl(this.participant.card_mask?.id));
            this.buildForm();
        } else if (this.creditRole === 'sender') {
            this.buildForm();
        }
        this.applyFullCardNumberValidator();
        this.form.controls.cardMaskNumber?.valueChanges
            .pipe(filter(value => !!value && value.length >= 6))
            .subscribe(cardNumber => this.updateCardIssuerBin(cardNumber.replace(/[#_\s]/g, '')));
        this.subscribeToRequisiteConflict();
    }

    ngAfterViewInit(): void {
        this.syncAllDictionarySelectors();
        this.updatePhoneNumberValue();
        this.dictionarySelectors.changes.subscribe(() => {
            this.updatePhoneNumberValue();
            this.syncAllDictionarySelectors();
        });
    }

    ngOnDestroy(): void {
        this.accountIssuerBinSubscription?.unsubscribe();
        this.accountRequisiteRefreshSubscription?.unsubscribe();
        this.cashRequisiteSubscription?.unsubscribe();
        this.notMyClientAvailabilitySubscription?.unsubscribe();
        this.notMyClientSubscription?.unsubscribe();
        this.paymentPointSubscription?.unsubscribe();
        this.paymInstrTypeSubscription?.unsubscribe();
        this.requisiteTypeSubscription?.unsubscribe();
        this.requisiteLockSubscription?.unsubscribe();
        this.requisiteConflictSubscription?.unsubscribe();
        this.roleSubscription?.unsubscribe();
        this.subjectTypeSubscription?.unsubscribe();
        this.dismissRequisiteConflictSnackbar();
    }

    buildForm(): void {
        if (!this.form.get('subjectType')) {
            this.form.addControl('subjectType', new FormControl(null, [Validators.required]));
        }
        this.subscribeToSubjectType();
        if (this.creditRole === 'sender') {
            this.form.controls.subjectType.setValue(SubjectType.ORGANIZATION);
            this.applyCreditSenderOrganization();
        }
        if (this.participant && !this.form.get('subjectType')?.value) {
            this.form.controls.subjectType.setValue(this.participant.type.toUpperCase() as SubjectType);
        }
    }

    toggleCollapsed(): void {
        this.collapsed = !this.collapsed;
    }

    requisiteBlockTemplate(block: RequisiteType): TemplateRef<unknown> | null {
        switch (block) {
            case RequisiteType.PAYMENT_POINT:
                return this.paymentPointBlockTemplate ?? null;
            default:
                return null;
        }
    }

    onCardInput(event: Event): void {
        const raw = (event.target as HTMLInputElement).value.toUpperCase();
        this.form.controls.cardMaskNumber.setValue(raw, {emitEvent: false});
        const input = raw.replace(/[#_]/g, '');
        this.updateCardIssuerBin(input);
    }

    onFullCardInput(event: Event): void {
        const control = this.form.controls.cardMaskNumber;
        const inputEl = event.target as HTMLInputElement;
        const raw = inputEl.value.replace(/\D/g, '').substring(0, CARD_NUMBER_MAX_LENGTH);
        const formatted = raw.match(/.{1,4}/g)?.join(' ') || '';
        control.setValue(formatted, {emitEvent: false});
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity({emitEvent: false});
        inputEl.value = formatted;
        setTimeout(() => {
            const newCursor = this.calculateCursorPosition(raw);
            inputEl.setSelectionRange(newCursor, newCursor);
        });
        this.updateCardIssuerBin(raw);
    }

    onIbanInput(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const cleaned = inputElement.value.toUpperCase()
            .replace(/[^A-Z0-9]/g, '')
            .slice(0, KZ_IBAN_LENGTH);
        this.form.controls.iban.setValue(cleaned, {emitEvent: false});
        this.form.controls.iban.markAsTouched();
        const isAccountRequisite = this.isAccountValueRequisite();
        if (!this.isDictionaryReady(this.binDictionary) || cleaned.length < 7) {
            if (isAccountRequisite) {
                this.ibanDependentValidation(false);
            }
            return;
        }
        const binCode = cleaned.substring(4, 7);
        const match = this.binDictionary.find(i => i.code2 === binCode);
        if (match) {
            this.form.controls.accountIssuerBin.setValue(match.code, {emitEvent: false});
            this.applyRequisiteOrganizationTypeFromDictionary(match);
            this.setSelectorValue(this.accountIssuerBinSelector, match);
            this.applyCardOrganizationFromIban(match);
        } else {
            this.form.controls.accountIssuerBin.setValue(null, {emitEvent: false});
            this.setSelectorValue(
                this.accountIssuerBinSelector,
                undefined,
                this.binDictionary.filter(item => item.code1 === 'BANK' && item.code2 != null),
            );
        }
        if (isAccountRequisite) {
            this.ibanDependentValidation(true);
            this.applyAccountFieldsAccess();
            this.clearAccountFieldsForNonOwner();
        }
        this.updateNotMyClientAvailability();
        this.refreshRequiredState();
        this.cdr.detectChanges();
    }

    onForeignIbanInput(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const cleaned = inputElement.value.toUpperCase()
            .replace(/[^A-Z0-9]/g, '')
            .slice(0, IBAN_MAX_LENGTH);
        this.form.controls.foreignIban.setValue(cleaned, {emitEvent: false});
        this.form.controls.foreignIban.markAsTouched();
    }

    onForeignIssuerBinInput(event: Event): void {
        const cleaned = (event.target as HTMLInputElement).value.toUpperCase();
        this.form.controls.foreignIssuerBin.setValue(cleaned, {emitEvent: false});
        this.form.controls.foreignIssuerBin.markAsTouched();
    }

    onWalletNumberInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const cleaned = input.value
            .replace(/[^0-9A-Za-z@_.+/:-]/g, '')
            .slice(0, WALLET_NUMBER_MAX_LENGTH);
        if (this.form.controls.walletNumber) {
            this.form.controls.walletNumber.setValue(cleaned);
            this.form.controls.walletNumber.markAsTouched();
        }
    }

    paymentPointNumberInput(event: EventTarget): void {
        const inputElement = event as HTMLInputElement;
        const cleaned = inputElement.value
            .toUpperCase()
            .replace(/[^A-Z0-9 ]/g, '')
            .replace(/\s{2,}/g, ' ')
            .slice(0, PAYMENT_POINT_MAX_LENGTH);
        this.form.controls.paymentPointNumber.setValue(cleaned, {emitEvent: false});
        this.form.controls.paymentPointNumber.markAsTouched();
    }

    disableEditSubjectType(): boolean {
        if (this.creditRole === 'sender') {
            this.form.get('subjectType')?.disable({emitEvent: false});
            return true;
        }
        if (this.lockedSubjectType) {
            this.form.get('subjectType')?.disable({emitEvent: false});
            return true;
        }
        const isNew = !this.participant?.type;
        return this.setControlAccess('subjectType', isNew);
    }

    disableEditRole(): boolean {
        if (this.creditRole) {
            this.form.get('role')?.disable({emitEvent: false});
            return true;
        }
        const isNew = !this.participant?.incident_role;
        return this.setControlAccess('role', isNew);
    }

    isIdentificationNumberReadonly(): boolean {
        if (this.authHolderService.hasAccess(['npck-admin'])) {
            return false;
        }
        return !!this.participant?.identification_number;
    }

    isNpk(): boolean {
        return this.authHolderService.hasAccess(['npck-admin']);
    }

    isOwnedIban(): boolean {
        if (!this.isDictionaryReady(this.binDictionary)) {
            return false;
        }
        const userBankCode = this.binDictionary.find(i => i.code === this.authHolderService.organizationBin)?.code2;
        if (!userBankCode) {
            return false;
        }
        const issuerBin = this.form.get('accountIssuerBin')?.value;
        if (issuerBin) {
            const issuerBankCode = this.binDictionary.find(i => i.code === issuerBin)?.code2 ?? issuerBin;
            return issuerBankCode === userBankCode;
        }
        const iban = this.form.controls.iban?.value;
        if (!iban || iban.length < 7) {
            return false;
        }
        return iban.substring(4, 7) === userBankCode;
    }

    areAccountFieldsVisible(): boolean {
        return !(this.isMvd() && this.isFraudOrOtherIncident());
    }

    isIbanReadonly(): boolean {
        if (this.isNpk() || !this.isFraudOrOtherIncident()) {
            return false;
        }
        if (this.isMvd()) {
            return this.isPersistedAccountWithIssuer();
        }
        return false;
    }

    isPersistedAccountWithIssuer(): boolean {
        return !!this.form.get('accountId')?.value && !!this.form.get('accountIssuerBin')?.value;
    }

    private applyCreditSenderOrganization(): void {
        const binControl = this.form.get('bin');
        const organizationNameControl = this.form.get('organizationName');
        const bin = this.participant?.identification_number ?? this.authHolderService.organizationBin ?? null;
        binControl?.setValue(bin, {emitEvent: false});
        binControl?.disable({emitEvent: false});
        organizationNameControl?.disable({emitEvent: false});
        const savedName = this.participant?.info?.name;
        if (savedName) {
            organizationNameControl?.setValue(savedName, {emitEvent: false});
            return;
        }
        this.dictionaryHolderService.getReference('bin').subscribe(bins => {
            const orgName = bins.find(item => item.code === bin)?.name_ru
                ?? this.authHolderService.currentOrgInfo?.name_ru ?? null;
            organizationNameControl?.setValue(orgName, {emitEvent: false});
        });
    }

    private setControlAccess(controlPath: string, isNew: boolean): boolean {
        if (this.authHolderService.hasAccess(['npck-admin'])) {
            return false;
        }
        const control = this.form?.get(controlPath);
        if (control) {
            isNew ? control.enable({emitEvent: false}) : control.disable({emitEvent: false});
        }
        return !isNew;
    }

    private updateCardIssuerBin(input: string): void {
        if (!this.isDictionaryReady(this.cardBinDictionary)) {
            return;
        }
        this.cardIssuerBinFromIban = false;
        const cardBinRef = input.length >= 6 ? this.cardBinDictionary.find(i => i.code === input.substring(0, 6)) : undefined;
        const match = cardBinRef ? this.binDictionary.find(i => i.code === cardBinRef.code1) : undefined;
        if (match) {
            this.form.controls.cardIssuerBin.setValue(match.code);
            this.applyRequisiteOrganizationTypeFromDictionary(match);
            this.setSelectorValue(this.cardBinIssuerBinSelector, match);
        } else {
            this.form.controls.cardIssuerBin.setValue(null);
            this.setSelectorValue(
                this.cardBinIssuerBinSelector,
                undefined,
                this.binDictionary.filter(item => item.code1 === 'BANK'),
            );
        }
        this.cdr.detectChanges();
    }

    private applyCardOrganizationFromIban(match: ReferenceBookResponse): void {
        if (this.form.controls.requisiteType?.value !== RequisiteType.CARD_MASK) {
            return;
        }
        const cardIssuerBinControl = this.form.controls.cardIssuerBin;
        if (!cardIssuerBinControl || (cardIssuerBinControl.value && !this.cardIssuerBinFromIban)) {
            return;
        }
        this.cardIssuerBinFromIban = true;
        cardIssuerBinControl.setValue(match.code, {emitEvent: false});
        this.setSelectorValue(this.cardBinIssuerBinSelector, match);
    }

    private calculateCursorPosition(raw: string): number {
        const digitIndex = raw.length;
        const spaceCount = Math.floor(digitIndex / 4);
        return digitIndex + spaceCount;
    }

    private isPaymentPointBlockActive(): boolean {
        return this.form.controls.requisiteType?.value === RequisiteType.PAYMENT_POINT
            || this.extraRequisiteBlocks.includes(RequisiteType.PAYMENT_POINT);
    }

    private subscribeToPaymentPointDependentValidation(): void {
        this.paymentPointSubscription?.unsubscribe();
        this.paymentPointDependentValidation();
        this.paymentPointSubscription = merge(
            this.form.controls.paymentPointIssuerBin.valueChanges,
            this.form.controls.requisiteType.valueChanges,
        ).subscribe(() => this.paymentPointDependentValidation());
    }

    private paymentPointDependentValidation(): void {
        const numberControl = this.form.controls.paymentPointNumber;
        const addressControl = this.form.controls.paymentPointAddress;
        const streetControl = this.form.controls.paymentPointStreet;
        const houseNumberControl = this.form.controls.paymentPointHouseNumber;
        if (!numberControl || !addressControl) {
            return;
        }
        const isPaymentPoint = this.isPaymentPointBlockActive();
        const hasIssuerBin = isPaymentPoint && !!this.form.controls.paymentPointIssuerBin?.value;
        const cashPaymentPointRequired = this.cashRole ? this.isCashPaymentPointRequired() : false;
        const paymentPointRequiredAllowed = this.cashRole ? cashPaymentPointRequired : true;
        const paymentPointActive = hasIssuerBin || cashPaymentPointRequired;
        if (paymentPointActive) {
            const requiredValidators = this.isNotMyClient || !paymentPointRequiredAllowed ? [] : [Validators.required];
            numberControl.enable({emitEvent: false});
            numberControl.setValidators([
                ...requiredValidators,
                Validators.maxLength(MAX_PAYMENT_POINT_NUMBER),
                Validators.pattern(PATTERN_PAYMENT_POINT_NUMBER),
            ]);
            numberControl.markAsTouched();
            addressControl.enable({emitEvent: false});
            addressControl.setValidators(requiredValidators);
            addressControl.markAsTouched();
            streetControl?.enable({emitEvent: false});
            houseNumberControl?.enable({emitEvent: false});
        } else {
            numberControl.disable({emitEvent: false});
            numberControl.setValidators([
                Validators.maxLength(MAX_PAYMENT_POINT_NUMBER),
                Validators.pattern(PATTERN_PAYMENT_POINT_NUMBER),
            ]);
            addressControl.disable({emitEvent: false});
            addressControl.clearValidators();
            streetControl?.disable({emitEvent: false});
            houseNumberControl?.disable({emitEvent: false});
        }
        numberControl.updateValueAndValidity();
        addressControl.updateValueAndValidity();
        streetControl?.updateValueAndValidity();
        houseNumberControl?.updateValueAndValidity();
    }

    private collectRequisiteIssuerBins(): string[] {
        return Object.values(REQUISITE_ISSUER_BIN_CONTROL)
            .map(controlName => this.form.get(controlName)?.value)
            .filter((bin): bin is string => !!bin);
    }

    private isUserOrganizationAmongIssuerBins(): boolean {
        const organizationBin = this.authHolderService.organizationBin;
        return !!organizationBin && this.collectRequisiteIssuerBins().includes(organizationBin);
    }

    private isCashRequiredTarget(): boolean {
        if (!this.cashTypeSelected || this.isNotMyClient) {
            return false;
        }
        if (this.isMvd()) {
            return this.form.get('role')?.value === 'victim';
        }
        if (this.isBank()) {
            return this.isUserOrganizationAmongIssuerBins();
        }
        return false;
    }

    private isTransferRequiredTarget(): boolean {
        if (!this.transferTypeSelected || this.isNotMyClient) {
            return false;
        }
        if (this.isMvd()) {
            return this.form.get('role')?.value === 'victim';
        }
        if (this.isBank()) {
            return this.isUserOrganizationAmongIssuerBins();
        }
        return false;
    }

    private isTransferCardIbanRequired(): boolean {
        return this.isTransferRequiredTarget()
            && this.isBank()
            && this.form.controls.requisiteType?.value === RequisiteType.CARD_MASK;
    }

    private isPhoneTransferType(): boolean {
        return this.transactionType === TransactionType.P2P || this.transactionType === TransactionType.P2PO;
    }

    private isPaymentSuspectPhoneRequired(): boolean {
        return !!this.paymentRole
            && this.paymentOrganizationInvolved
            && this.isBank()
            && !this.isNotMyClient
            && this.form.get('role')?.value === 'suspect';
    }

    private isCashIbanRequired(): boolean {
        if (!this.cashRole || this.isNotMyClient) {
            return false;
        }
        if (this.isMvd()) {
            return this.cashRole === 'withdrawal-sender'
                && this.transactionType === TransactionType.CW
                && this.form.get('role')?.value === 'victim';
        }
        return this.isBank() && this.isUserOrganizationAmongIssuerBins();
    }

    private isCashPaymentPointRequired(): boolean {
        if (!this.cashRole || this.isNotMyClient || this.transactionType === TransactionType.TOPEWT) {
            return false;
        }
        return this.isBank() && this.isUserOrganizationAmongIssuerBins();
    }

    private applyFullCardNumberValidator(): void {
        const control = this.form.controls.cardMaskNumber;
        if (!control || (!this.isOtherIncidentType && !this.isCompromisedPaymentInstrumentType)) {
            return;
        }
        control.addValidators(this.fullCardNumberValidator);
        control.updateValueAndValidity({emitEvent: false});
    }

    private updateRequiredValidator(control: AbstractControl | null | undefined, required: boolean): void {
        if (!control) {
            return;
        }
        if (required) {
            control.addValidators(Validators.required);
        } else {
            control.removeValidators(Validators.required);
        }
        control.updateValueAndValidity({emitEvent: false});
    }

    private applyTargetExtraFields(): void {
        const isPerson = this.form.get('subjectType')?.value === SubjectType.PERSON;
        const cashRequired = this.isCashRequiredTarget() && isPerson;
        const personRequired = cashRequired || (this.isTransferRequiredTarget() && isPerson);
        this.updateRequiredValidator(this.form.get('gender'), personRequired);
        this.updateRequiredValidator(this.form.get('country'), cashRequired);
        if (this.mode !== 'refund') {
            this.updateRequiredValidator(this.form.get('originDate')?.get('date'), personRequired);
        }
    }

    private applyRequisiteValueRequired(): void {
        const requisiteType = this.isNotMyClient ? null : this.form.controls.requisiteType?.value;
        if (!this.isCompromisedPaymentInstrumentType) {
            this.updateRequiredValidator(this.form.controls.cardMaskNumber, requisiteType === RequisiteType.CARD_MASK);
            this.updateRequiredValidator(this.form.controls.walletNumber, requisiteType === RequisiteType.ELECTRONIC_WALLET);
            this.updateRequiredValidator(this.form.controls.foreignIban, requisiteType === RequisiteType.ACCOUNT_FOREIGN_BANK);
        }
        this.updateRequiredValidator(this.form.controls.paymentPointIssuerBin, requisiteType === RequisiteType.PAYMENT_POINT);
        this.applyIbanRequiredForBank();
    }

    private refreshRequiredState(): void {
        if (!this.form?.controls?.requisiteType) {
            return;
        }
        const role = this.form.get('role')?.value;
        if (role) {
            this.handleRoleChange(role);
        } else {
            this.applyRequisiteValueRequired();
            this.applyTargetExtraFields();
        }
        this.paymentPointDependentValidation();
    }

    private subscribeToCashRequisiteRequired(): void {
        this.cashRequisiteSubscription?.unsubscribe();
        this.refreshRequiredState();
        this.cashRequisiteSubscription = merge(
            this.form.controls.requisiteType.valueChanges,
            this.form.controls.accountIssuerBin.valueChanges,
            this.form.controls.cardIssuerBin.valueChanges,
            this.form.controls.walletIssuerBin.valueChanges,
            this.form.controls.paymentPointIssuerBin.valueChanges,
            this.form.controls.foreignIssuerBin.valueChanges,
        ).subscribe(() => this.refreshRequiredState());
    }

    private subscribeToNotMyClientAvailability(): void {
        this.notMyClientAvailabilitySubscription?.unsubscribe();
        this.updateNotMyClientAvailability();
        this.notMyClientAvailabilitySubscription = merge(
            this.form.controls.requisiteType.valueChanges,
            this.form.controls.accountIssuerBin.valueChanges,
            this.form.controls.cardIssuerBin.valueChanges,
            this.form.controls.walletIssuerBin.valueChanges,
            this.form.controls.paymentPointIssuerBin.valueChanges,
            this.form.controls.foreignIssuerBin.valueChanges,
            this.form.controls.iban.valueChanges,
            this.form.controls.foreignIban.valueChanges,
            this.form.controls.cardMaskNumber.valueChanges,
            this.form.controls.walletNumber.valueChanges,
            this.form.controls.paymentPointNumber.valueChanges,
            this.form.controls.paymentPointAddress.valueChanges,
        ).subscribe(() => this.updateNotMyClientAvailability());
    }

    private updateNotMyClientAvailability(): void {
        const control = this.form.controls.notMyClient;
        if (!control) {
            return;
        }
        if (this.canSetNotMyClient()) {
            control.enable({emitEvent: false});
            return;
        }
        if (control.value) {
            control.setValue(false);
        }
        control.disable({emitEvent: false});
    }

    private canSetNotMyClient(): boolean {
        if (!this.participant || !this.authHolderService.hasAccess(['finance-user'])) {
            return false;
        }
        const organizationBin = this.authHolderService.organizationBin;
        if (!organizationBin) {
            return false;
        }
        const requisites = createAllRequisitesV2(this.form.getRawValue());
        return [
            requisites.account?.issuerBin,
            requisites.foreignAccount?.issuerBin,
            requisites.accountCard?.issuerBin,
            requisites.wallet?.issuerBin,
            requisites.paymentPoint?.issuerBin,
        ].some(issuerBin => !!issuerBin && issuerBin === organizationBin);
    }

    private subscribeToNotMyClient(): void {
        this.notMyClientSubscription?.unsubscribe();
        this.notMyClientSubscription = this.form.controls.notMyClient.valueChanges.subscribe(value => {
            if (value) {
                this.stripRequiredValidators();
            } else {
                this.form.controls.subjectType.setValue(this.form.controls.subjectType.value);
            }
        });
    }

    private stripRequiredValidators(): void {
        Object.entries(this.form.controls).forEach(([controlName, control]) => {
            if (NOT_MY_CLIENT_ALWAYS_REQUIRED_CONTROLS.includes(controlName)) {
                return;
            }
            this.removeRequiredValidatorDeep(control);
        });
        this.form.updateValueAndValidity({emitEvent: false});
        this.cdr.detectChanges();
    }

    private removeRequiredValidatorDeep(control: AbstractControl): void {
        if (control instanceof FormGroup) {
            Object.values(control.controls).forEach(child => this.removeRequiredValidatorDeep(child));
        }
        control.removeValidators(Validators.required);
        control.updateValueAndValidity({emitEvent: false});
    }

    private ensureAccountFieldControls(): void {
        const accountFields = {
            accountCurrency: this.participant?.account?.currency,
            accountBalance: this.participant?.account?.balance,
            accountStatus: this.participant?.account?.status,
        };
        if (this.mode === 'refund') {
            delete accountFields.accountBalance;
            delete accountFields.accountStatus;
        }
        const form = this.form as FormGroup;
        Object.entries(accountFields).forEach(([key, value]) => {
            if (form.get(key)) {
                return;
            }
            const validators = key === 'accountBalance' ? [Validators.min(0)] : [];
            form.addControl(key, new FormControl(value, validators));
        });
    }

    private ibanDependentValidation(shouldRequire: boolean): void {
        this.ensureAccountFieldControls();
        const issuerBinControl = this.form.get('accountIssuerBin');
        const accountStatusControl = this.form.get('accountStatus');
        const accountBalanceControl = this.form.get('accountBalance');
        const accountCurrencyControl = this.form.get('accountCurrency');
        if (!issuerBinControl || !accountStatusControl || !accountBalanceControl || !accountCurrencyControl) {
            return;
        }
        const issuerBinRequired = (shouldRequire && this.form.controls.requisiteType?.value === RequisiteType.ACCOUNT_KZ)
            || this.creditRole === 'receiver';
        this.applyRequired(issuerBinControl, issuerBinRequired);
        const required = shouldRequire && this.isAccountFieldsRequired();
        this.applyRequired(accountStatusControl, required);
        this.applyRequired(accountCurrencyControl, required);
        this.applyRequired(accountBalanceControl, required, [Validators.min(0)]);
    }

    private applyRequired(control: AbstractControl, required: boolean, keep: ValidatorFn[] = []): void {
        const shouldRequire = required && !this.isNotMyClient;
        control.setValidators(shouldRequire ? [Validators.required, ...keep] : [...keep]);
        if (shouldRequire) {
            control.markAsTouched();
        }
        control.updateValueAndValidity({emitEvent: false});
    }

    private isAccountFieldsRequired(): boolean {
        if (this.creditRole) {
            return false;
        }
        if (this.isFraudOrOtherIncident()) {
            return this.isAccountFieldRequiredByMatrix();
        }
        return this.form.get('role')?.value === 'suspect' || this.isCompromisedPaymentInstrumentType;
    }

    private isAccountFieldRequiredByMatrix(): boolean {
        if (!this.isBank() || !this.isOwnedIban()) {
            return false;
        }
        const isVictim = this.form.get('role')?.value === 'victim';
        if (this.participantSide === 'sender') {
            return isVictim && this.transactionStatus === 'A';
        }
        return this.transactionStatus === 'C' || this.transactionStatus === 'B';
    }

    private applyAccountFieldsAccess(): void {
        if (this.creditRole || !this.isFraudOrOtherIncident()) {
            return;
        }
        const readonly = !this.isNpk() && !this.isOwnedIban();
        ['accountCurrency', 'accountStatus', 'accountBalance'].forEach(name => {
            const control = this.form.get(name);
            if (!control) {
                return;
            }
            readonly ? control.disable({emitEvent: false}) : control.enable({emitEvent: false});
        });
    }

    private clearAccountFieldsForNonOwner(): void {
        if (!this.isFraudOrOtherIncident() || this.isNpk() || this.isOwnedIban()) {
            return;
        }
        ['accountCurrency', 'accountStatus', 'accountBalance'].forEach(name => {
            this.form.get(name)?.setValue(null, {emitEvent: false});
        });
    }

    private isAccountValueRequisite(): boolean {
        const requisiteType = this.form.controls.requisiteType?.value;
        return requisiteType === RequisiteType.ACCOUNT_KZ
            || requisiteType === RequisiteType.CARD_MASK
            || (requisiteType === RequisiteType.ELECTRONIC_WALLET && this.showWalletIbanField);
    }

    private refreshAccountFieldsState(): void {
        if (this.isRefreshingAccountFields || !this.isAccountValueRequisite()) {
            return;
        }
        this.isRefreshingAccountFields = true;
        try {
            const iban = this.form.controls.iban?.value;
            this.applyIbanRequiredForBank();
            this.applyIssuerBinLockForMvd();
            this.ibanDependentValidation(!!iban && iban.length >= 7);
            this.applyAccountFieldsAccess();
        } finally {
            this.isRefreshingAccountFields = false;
        }
    }

    private applyIbanRequiredForBank(): void {
        const ibanControl = this.form.controls.iban;
        if (!ibanControl) {
            return;
        }
        const accountRequisiteIbanRequired = this.form.controls.requisiteType?.value === RequisiteType.ACCOUNT_KZ
            && !(this.isPhoneTransferType() && this.mode !== 'refund' && !this.isCompromisedPaymentInstrumentType);
        const required = accountRequisiteIbanRequired
            || this.creditRole === 'receiver'
            || this.isCashIbanRequired()
            || this.isTransferCardIbanRequired();
        this.applyRequired(ibanControl, required, [
            Validators.minLength(KZ_IBAN_LENGTH),
            Validators.maxLength(KZ_IBAN_LENGTH),
            Validators.pattern(KZ_IBAN_PATTERN),
        ]);
        ibanControl.updateValueAndValidity({emitEvent: false});
    }

    private applyIssuerBinLockForMvd(): void {
        const issuerBinControl = this.form.get('accountIssuerBin');
        if (!issuerBinControl) {
            return;
        }
        if (this.isMvd() && this.isFraudOrOtherIncident() && this.isPersistedAccountWithIssuer()) {
            issuerBinControl.disable({emitEvent: false});
        } else {
            issuerBinControl.enable({emitEvent: false});
        }
    }

    private buildRequisiteOrganizationUnityValidator(): ValidatorFn {
        return control => {
            if (this.mode === 'refund') {
                return null;
            }
            const cardNumber = control.get('cardMaskNumber')?.value;
            const iban = control.get('iban')?.value;
            if (!cardNumber || !iban) {
                return null;
            }
            const cardOrganization = this.resolveOrganizationBin(control.get('cardIssuerBin')?.value);
            const accountOrganization = this.resolveOrganizationBin(control.get('accountIssuerBin')?.value);
            if (!cardOrganization || !accountOrganization || cardOrganization === accountOrganization) {
                return null;
            }
            return {requisiteOrganizationConflict: true};
        };
    }

    private subscribeToRequisiteConflict(): void {
        this.notifyOnNewRequisiteConflict();
        this.requisiteConflictSubscription = this.form.statusChanges.subscribe(() => this.notifyOnNewRequisiteConflict());
    }

    private notifyOnNewRequisiteConflict(): void {
        const hasConflict = this.form.hasError('requisiteOrganizationConflict');
        if (hasConflict && !this.hadRequisiteConflict) {
            const conflictMessageKey = this.cashTypeSelected
                ? 'i18n_af_errorCashRequisiteOrganizationConflict'
                : 'i18n_af_errorRequisiteOrganizationConflict';
            this.requisiteConflictSnackbarRef = this.snackbarService.showSnackBar(conflictMessageKey, 'error', true);
        }
        if (!hasConflict && this.hadRequisiteConflict) {
            this.dismissRequisiteConflictSnackbar();
        }
        this.hadRequisiteConflict = hasConflict;
    }

    private dismissRequisiteConflictSnackbar(): void {
        this.requisiteConflictSnackbarRef?.dismiss();
        this.requisiteConflictSnackbarRef = undefined;
    }

    private resolveOrganizationBin(value: string | null | undefined): string | null {
        if (!value) {
            return null;
        }
        return this.binDictionary.find(i => i.code === value)?.code
            ?? this.binDictionary.find(i => i.code2 === value)?.code
            ?? value;
    }

    private applyRequisiteOrganizationTypeFromDictionary(match: ReferenceBookResponse): void {
        const organizationType = this.organizationTypes.find(i => i.code === match.code1);
        if (organizationType) {
            this.form.controls.requisiteOrganizationType?.setValue(organizationType.code, {emitEvent: false});
        }
    }

    private applyRequisiteOrganizationTypeRequired(): void {
        const control = this.form.controls.requisiteOrganizationType;
        if (!control) {
            return;
        }
        const requisiteType = this.form.controls.requisiteType?.value;
        const required = this.transferTypeSelected && this.isBank()
            && (requisiteType === RequisiteType.CARD_MASK || requisiteType === RequisiteType.ACCOUNT_KZ);
        this.applyRequired(control, required);
    }

    private loadDictionaries(): void {
        this.dictionaryHolderService.getReference('bin').subscribe(items => {
            const paymentPointIssuerBin = this.participant?.paymentPoint?.issuer_bin;
            this.binDictionary = items;
            if (paymentPointIssuerBin) {
                const binMatch = this.binDictionary.find(i => i.code === paymentPointIssuerBin);
                const orgTypeMatch = this.organizationTypes.find(i => i.code === binMatch?.code1);
                if (orgTypeMatch) {
                    this.form.controls.paymentPointType.setValue(orgTypeMatch.code);
                }
            }
            const requisiteIssuerBin = this.participant?.account?.issuerBin ?? this.participant?.card_mask?.issuer_bin;
            if (requisiteIssuerBin && !this.form.controls.requisiteOrganizationType?.value) {
                const requisiteBinMatch = this.binDictionary.find(i => i.code === requisiteIssuerBin || i.code2 === requisiteIssuerBin);
                if (requisiteBinMatch) {
                    this.applyRequisiteOrganizationTypeFromDictionary(requisiteBinMatch);
                }
            }
            this.refreshAccountFieldsState();
        });
        this.dictionaryHolderService.getReference('card_bin').subscribe(items => {
            this.cardBinDictionary = items;
            const issuerBin = this.participant?.card_mask?.issuer_bin;
            if (issuerBin) {
                const match = this.binDictionary.find(i => i.code === issuerBin);
                if (match) {
                    this.setSelectorValue(this.cardBinIssuerBinSelector, match);
                }
            }
        });
        this.cdr.detectChanges();
    }

    private syncAllDictionarySelectors(): void {
        this.dictionarySelectors.forEach(selector => {
            if (selector.control === this.form.controls.accountIssuerBin) {
                this.updateSelectorFromFormValue('accountIssuerBin', selector, this.binDictionary);
            }
            if (selector.control === this.form.controls.cardIssuerBin) {
                this.updateSelectorFromFormValue('cardIssuerBin', selector, this.binDictionary);
            }
            if (selector.control === this.form.controls.paymentPointIssuerBin) {
                this.updateSelectorFromFormValue('paymentPointIssuerBin', selector, this.binDictionary);
            }
        });
    }

    private restoreDependentSelectorsState(): void {
        const paymentPointTypeControl = this.form.controls.paymentPointType;
        if (paymentPointTypeControl?.value) {
            paymentPointTypeControl.setValue(paymentPointTypeControl.value);
        }
        this.syncAllDictionarySelectors();
    }

    private updatePhoneNumberValue(): void {
        const phoneNumber = this.participant?.phone_number?.number;
        const phoneControl = this.form.controls.phoneNumber;
        if (phoneNumber && phoneControl) {
            phoneControl.setValue(normalizePhonePrefix(phoneNumber));
        }
        this.cdr.detectChanges();
    }

    private subscribeToSubjectType(): void {
        this.subjectTypeSubscription?.unsubscribe();
        this.subjectTypeSubscription = this.form.controls.subjectType.valueChanges.subscribe(value => {
            const snapshot = this.form.getRawValue();
            delete snapshot.subjectType;
            this.removeControllers();
            this.buildCommonForm();
            this.form.controls.notMyClient?.setValue(!!snapshot.notMyClient, {emitEvent: false});
            if (value === SubjectType.PERSON) {
                this.buildPersonForm();
            } else {
                this.buildOrganizationForm();
            }
            this.subscribeToRoleChanges();
            const restoredRole = (snapshot as {role?: string}).role;
            if (restoredRole) {
                this.form.get('role')?.setValue(restoredRole, {emitEvent: false});
                this.handleRoleChange(restoredRole);
            }
            if (this.requisiteTypeModeLocked) {
                delete snapshot.requisiteType;
            }
            this.form.patchValue(snapshot, {emitEvent: false});
            this.updateNotMyClientAvailability();
            this.restoreDependentSelectorsState();
            if (this.prefillRequisiteType && !this.form.controls.requisiteType?.value) {
                this.form.controls.requisiteType?.setValue(this.prefillRequisiteType);
            }
            if (!restoredRole && !this.participant?.id && this.creditRole !== 'receiver' && !this.paymentRole && !this.phoneRequired) {
                this.showPhoneComponent = false;
            }
            this.form.updateValueAndValidity();
            if (this.isNotMyClient) {
                this.stripRequiredValidators();
            }
            this.cdr.detectChanges();
        });
    }

    private subscribeToRoleChanges(): void {
        this.roleSubscription?.unsubscribe();
        const roleControl = this.form.controls.role;
        this.roleSubscription = roleControl.valueChanges
            .pipe(filter(value => !!value))
            .subscribe(value => {
                this.handleRoleChange(value);
            });
        roleControl.value && this.handleRoleChange(roleControl.value);
    }

    private removeControllers(): void {
        Object.keys(this.form.controls)
            .filter((controlName): controlName is keyof ParticipantForm => !['subjectType', 'id'].includes(controlName))
            .forEach(controlName => this.form.removeControl(controlName, {emitEvent: true}));
    }

    private buildCommonForm(): void {
        if (this.creditRole === 'sender') {
            this.form.addControl('role', new FormControl('victim', [Validators.required]));
            this.loadDictionaries();
            return;
        }
        const info = this.participant?.info;
        const date = info?.origin_date && dayjs(info.origin_date).toDate();
        this.form.addControl('originDate', this.formBuilder.group({
            date: new FormControl(date),
        }));
        this.form.addControl('phoneNumberId', new FormControl(this.participant?.phone_number?.id));
        this.form.addControl('phoneNumber', new FormControl(this.participant?.phone_number?.number ?? null));
        this.form.addControl('country', new FormControl(null));
        this.form.addControl('selectedPhoneInfo', new FormControl(null));
        this.form.addControl('role', new FormControl(
            this.participant?.incident_role?.toLowerCase() ?? (this.creditRole ? 'victim' : null),
            [Validators.required]
        ));
        this.form.addControl('notMyClient', new FormControl(this.participant?.notMyClient ?? false));
        this.subscribeToNotMyClient();
        this.buildRequisiteControls();
        this.subscribeToNotMyClientAvailability();
        this.setupCompromisedPaymentInstrumentValidation();
        this.loadDictionaries();
        if (this.mode === 'refund') {
            this.form.controls.originDate.controls.date.setValidators([Validators.required]);
        }
    }

    private buildPersonForm(): void {
        const info = this.participant?.info;
        if (!this.form.get('name')) {
            this.form.addControl('name', new FormControl(info?.name));
        }
        if (!this.form.get('gender')) {
            this.form.addControl('gender', new FormControl(null));
        }
        if (!this.form.get('iin')) {
            this.form.addControl('iin', this.buildIdNumberControl(this.participant?.identification_number));
        }
        if (!this.form.get('surname')) {
            this.form.addControl('surname', new FormControl(info?.surname));
        }
        if (!this.form.get('patronymic')) {
            this.form.addControl('patronymic', new FormControl(info?.patronymic));
        }
    }

    private buildOrganizationForm(): void {
        if (!this.form.get('bin')) {
            this.form.addControl('bin', this.buildIdNumberControl(this.participant?.identification_number));
        }
        if (!this.form.get('organizationName')) {
            this.form.addControl('organizationName', new FormControl(this.participant?.info?.name));
        }
    }

    private buildIdNumberControl(value?: string): FormControl<string | null> {
        return new FormControl(value ?? null, [
            Validators.pattern(IIN_PATTERN),
            Validators.minLength(12),
            Validators.maxLength(12),
        ]);
    }

    private isDictionaryReady(dict: ReferenceBookResponse[]): boolean {
        return !!dict && dict.length > 0;
    }

    private isMvd(): boolean {
        return this.authHolderService.hasAccess(['government-user']);
    }

    private isBank(): boolean {
        return this.authHolderService.hasAccess(['finance-user']);
    }

    private isFraudOrOtherIncident(): boolean {
        return this.isIncidentType(IncidentType.FRAUD) || this.isIncidentType(IncidentType.OTHER);
    }

    private handleRoleChange(role: string): void {
        this.showPhoneComponent = true;
        const fields = this.form.get('subjectType')?.value == SubjectType.PERSON
            ? ['name', 'surname', 'iin'] : ['organizationName', 'bin'];
        if (this.creditRole !== 'sender') {
            fields.push('phoneNumber');
        }

        this.ensureAccountFieldControls();
        const isSuspect = role === 'suspect';
        const isCategoryTarget = this.isCashRequiredTarget() || this.isTransferRequiredTarget();
        const isCashCreateMinimal = this.cashRole === 'top-up-receiver' && !this.participant;
        const form = this.form as FormGroup;

        fields.forEach(field => {
            const control = form.get(field);
            if (control) {
                const required = isCategoryTarget || (!isCashCreateMinimal && !(isSuspect && this.mode !== 'refund'));
                if (required) {
                    control.addValidators(Validators.required);
                } else {
                    control.removeValidators(Validators.required);
                }
                control.updateValueAndValidity();
            }
        });

        if (this.phoneRequired || this.creditRole === 'receiver' || this.isPaymentSuspectPhoneRequired()) {
            const phoneControl = form.get('phoneNumber');
            phoneControl?.addValidators(Validators.required);
            phoneControl?.updateValueAndValidity();
        }

        this.applyTargetExtraFields();
        this.applyRequisiteValueRequired();
        this.refreshAccountFieldsState();
        if (this.isNotMyClient) {
            this.stripRequiredValidators();
        }
        this.cdr.detectChanges();
    }

    private buildRequisiteControls(): void {
        let requisiteType: string;

        if (this.mode === 'incident') {
            requisiteType = this.creditRole === 'receiver' ? RequisiteType.ACCOUNT_KZ : this.participant?.requisiteType;
        } else if (this.mode === 'refund') {
            requisiteType = RequisiteType.ACCOUNT_KZ;
        }

        this.requisiteTypeModeLocked = (this.mode === 'refund' && !!requisiteType) || this.creditRole === 'receiver';
        this.form.addControl('requisiteType', new FormControl({
            value: requisiteType ?? null,
            disabled: this.requisiteTypeModeLocked,
        }, this.isCompromisedPaymentInstrumentType ? [Validators.required] : []));
        this.form.addControl('accountId', new FormControl(this.participant?.account?.id ?? null));
        this.form.addControl('foreignAccountId', new FormControl(this.participant?.foreignAccount?.id ?? null));
        this.form.addControl('accountIssuerBin', new FormControl(this.participant?.account?.issuerBin ?? null));
        this.form.addControl('requisiteOrganizationType', new FormControl(null));
        this.form.addControl('walletNumber', new FormControl(this.participant?.wallet?.number ?? null, [
            Validators.minLength(WALLET_NUMBER_MIN_LENGTH),
            Validators.maxLength(WALLET_NUMBER_MAX_LENGTH),
            Validators.pattern(NUMBER_PATTERN),
        ]));
        this.form.addControl('walletIssuerBin', new FormControl(this.participant?.wallet?.issuer_bin ?? null));
        this.form.addControl('foreignIban', new FormControl(this.participant?.foreignAccount?.iban ?? null, [
            Validators.minLength(FOREIGN_IBAN_MIN_LENGTH),
            Validators.maxLength(IBAN_MAX_LENGTH),
            Validators.pattern(FOREIGN_IBAN_PATTERN),
        ]));
        this.form.addControl('iban', new FormControl(this.participant?.account?.iban ?? null, [
            Validators.minLength(KZ_IBAN_LENGTH),
            Validators.maxLength(KZ_IBAN_LENGTH),
            Validators.pattern(KZ_IBAN_PATTERN),
        ]));
        this.form.addControl('foreignIssuerBin', new FormControl(this.participant?.foreignAccount?.issuerBin ?? null));
        this.form.addControl('paymentPointType', new FormControl(null));
        this.form.addControl('paymentPointIssuerBin', new FormControl(this.participant?.paymentPoint?.issuer_bin ?? null));
        this.form.addControl('paymentPointNumber', new FormControl(this.participant?.paymentPoint?.number ?? null, [
            Validators.maxLength(MAX_PAYMENT_POINT_NUMBER),
            Validators.pattern(PATTERN_PAYMENT_POINT_NUMBER),
        ]));
        this.form.addControl('paymentPointAddress', new FormControl(this.participant?.paymentPoint?.address ?? null));
        this.form.addControl('paymentPointStreet', new FormControl(this.participant?.paymentPoint?.street ?? null));
        this.form.addControl('paymentPointHouseNumber', new FormControl(this.participant?.paymentPoint?.house_number ?? null));
        this.form.addControl('walletId', new FormControl(this.participant?.wallet?.id ?? null));
        this.form.addControl('paymentPointId', new FormControl(this.participant?.paymentPoint?.id ?? null));
        this.form.addControl('cardMaskNumberId', new FormControl(this.participant?.card_mask?.id));
        this.form.addControl('cardMaskNumber',
            new FormControl(this.participant?.card_mask?.card_number));
        this.form.addControl('cardIssuerBin', new FormControl(this.participant?.card_mask?.issuer_bin ?? null));
        if (this.mode === 'refund') {
            this.form.addControl('refundBin',
                new FormControl((this.participant as ParticipantViewModel)?.refundBin ?? null, [Validators.required]),
            );
            this.form.controls.iban.addValidators([Validators.required]);
        }
        if (this.creditRole === 'receiver') {
            this.form.controls.iban.addValidators(Validators.required);
            this.form.controls.accountIssuerBin.addValidators(Validators.required);
            this.form.controls.iban.updateValueAndValidity({emitEvent: false});
            this.form.controls.accountIssuerBin.updateValueAndValidity({emitEvent: false});
        }
        if (this.isCompromisedPaymentInstrumentType) {
            this.subscribeToRequisiteTypeChanges();
        }
        this.applyFullCardNumberValidator();
        this.subscribeToPaymentPointDependentValidation();
        this.subscribeToAccountIssuerBinChanges();
        this.subscribeToAccountRequisiteRefresh();
        this.subscribeToCashRequisiteRequired();
    }

    private subscribeToAccountIssuerBinChanges(): void {
        this.accountIssuerBinSubscription?.unsubscribe();
        const issuerBinControl = this.form.get('accountIssuerBin');
        if (!issuerBinControl) {
            return;
        }
        this.accountIssuerBinSubscription = issuerBinControl.valueChanges.subscribe(() => {
            this.refreshAccountFieldsState();
        });
    }

    private subscribeToAccountRequisiteRefresh(): void {
        this.accountRequisiteRefreshSubscription?.unsubscribe();
        this.accountRequisiteRefreshSubscription = this.form.controls.requisiteType.valueChanges.subscribe(() => {
            this.applyRequisiteOrganizationTypeRequired();
            if (this.creditRole || this.mode === 'refund') {
                return;
            }
            if (this.isAccountValueRequisite()) {
                this.refreshAccountFieldsState();
            } else {
                this.clearAccountRequisiteRequirements();
            }
        });
    }

    private clearAccountRequisiteRequirements(): void {
        const ibanControl = this.form.controls.iban;
        if (!ibanControl) {
            return;
        }
        this.applyRequired(ibanControl, false, [Validators.pattern(KZ_IBAN_PATTERN)]);
        this.ibanDependentValidation(false);
    }

    private subscribeToRequisiteTypeChanges(): void {
        this.requisiteTypeSubscription?.unsubscribe();
        this.updateRequisiteValidators(this.form.controls.requisiteType.value);
        this.applyRequisiteOrganizationTypeRequired();
        this.requisiteTypeSubscription = this.form.controls.requisiteType.valueChanges.subscribe(value => {
            this.updateRequisiteValidators(value);
            this.applyRequisiteOrganizationTypeRequired();
        });
        this.requisiteLockSubscription?.unsubscribe();
        this.requisiteLockSubscription = this.form.statusChanges.subscribe(() => this.updateRequisiteSwitchLock());
        this.updateRequisiteSwitchLock();
    }

    private requisiteControlNames(type: string): (keyof ParticipantForm)[] {
        switch (type) {
            case RequisiteType.ACCOUNT_KZ:
                return ['iban', 'accountIssuerBin', 'accountCurrency', 'accountStatus', 'accountBalance'];
            case RequisiteType.ACCOUNT_FOREIGN_BANK:
                return ['foreignIban', 'foreignIssuerBin'];
            case RequisiteType.CARD_MASK:
                return ['cardMaskNumber', 'cardIssuerBin'];
            case RequisiteType.ELECTRONIC_WALLET:
                return ['walletNumber', 'walletIssuerBin'];
            case RequisiteType.PAYMENT_POINT:
                return ['paymentPointNumber', 'paymentPointAddress', 'paymentPointIssuerBin', 'paymentPointType'];
            default:
                return [];
        }
    }

    private isCurrentRequisiteFilledButInvalid(): boolean {
        const type = this.form.controls.requisiteType?.value;
        if (!type) {
            return false;
        }
        const names = this.requisiteControlNames(type);
        const primaryValue = this.form.controls[names[0]]?.value;
        const isFilled = typeof primaryValue === 'string' ? !!primaryValue.trim() : primaryValue != null;
        if (!isFilled) {
            return false;
        }
        return names.some(name => this.form.controls[name]?.invalid);
    }

    private updateRequisiteSwitchLock(): void {
        if (this.requisiteTypeModeLocked) {
            return;
        }
        const control = this.form.controls.requisiteType;
        if (!control) {
            return;
        }
        if (this.isCurrentRequisiteFilledButInvalid()) {
            if (control.enabled) {
                control.disable({emitEvent: false});
            }
        } else if (control.disabled) {
            control.enable({emitEvent: false});
        }
    }

    private updateRequisiteValidators(requisiteType: string): void {
        if (this.isNotMyClient) {
            return;
        }
        const requisiteFieldMap: Record<string, AbstractControl> = {
            ACCOUNT_KZ: this.form.controls.iban,
            ACCOUNT_FOREIGN_BANK: this.form.controls.foreignIban,
            CARD_MASK: this.form.controls.cardMaskNumber,
            ELECTRONIC_WALLET: this.form.controls.walletNumber,
        };
        Object.entries(requisiteFieldMap).forEach(([type, control]) => {
            if (!control) {
                return;
            }
            if (type === requisiteType) {
                control.addValidators(Validators.required);
                control.markAsTouched();
            } else {
                control.removeValidators(Validators.required);
            }
            control.updateValueAndValidity();
        });
    }

    private updateSelectorFromFormValue(
        controlName: keyof ParticipantForm,
        selector: DictionarySelectorComponent,
        dictionary: ReferenceBookResponse[],
        matchBy: 'code' | 'code2' = 'code',
    ): void {
        const value = this.form.controls[controlName].value;
        const match = dictionary.find(item => item[matchBy] === value);
        if (match) {
            this.setSelectorValue(selector, match);
            this.cdr.detectChanges();
        }
    }

    private setSelectorValue(
        selector: DictionarySelectorComponent | undefined,
        match: ReferenceBookResponse | undefined,
        fallbackDictionary: ReferenceBookResponse[] = [],
    ): void {
        if (!selector) {
            return;
        }
        if (match) {
            selector.selectedItems = [match];
            selector.filteredDictionary = [match];
        } else {
            selector.selectedItems = [];
            selector.filteredDictionary = fallbackDictionary;
        }
        selector.updateDisplayedValue();
    }

    private readonly paymInstrRequisiteValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        const group = control as FormGroup<ParticipantForm>;
        const incidentType = group.root?.get('commonInformationForm.type')?.value;
        if (incidentType !== IncidentType.COMPROMISED_PAYMENT_INSTRUMENT) {
            return null;
        }
        const hasAnyRequisite = [
            group.controls.iban?.value,
            group.controls.foreignIban?.value,
            group.controls.cardMaskNumber?.value,
            group.controls.walletNumber?.value,
            group.controls.paymentPointNumber?.value,
        ]
            .map(v => (typeof v === 'string' ? v.trim() : v))
            .some(v => !!v);
        return hasAnyRequisite ? null : {paymInstrRequisiteRequired: true};
    };

    private readonly paymentVictimRequisiteValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        if (!this.paymentRole || this.isNotMyClient || (!this.isMvd() && !this.isBank())) {
            return null;
        }
        const group = control as FormGroup<ParticipantForm>;
        if (group.controls.role?.value !== 'victim') {
            return null;
        }
        const walletAllowed = this.transactionType === TransactionType.TOPCW || this.transactionType === TransactionType.TOPEWP;
        const hasRequisite = !!sanitizeCardNumber(group.controls.cardMaskNumber?.value)
            || !!group.controls.iban?.value?.trim()
            || (walletAllowed && !!group.controls.walletNumber?.value?.trim());
        return hasRequisite ? null : {paymentVictimRequisiteRequired: true};
    };

    private readonly creditReceiverPhoneValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        if (this.creditRole !== 'receiver') {
            return null;
        }
        const group = control as FormGroup<ParticipantForm>;
        const phone = group.controls.phoneNumber?.value;
        const phoneInfo = group.controls.selectedPhoneInfo?.value;
        const isValid = !!phone && !!phoneInfo && !!getValidPhoneNumberOrNull(phone, phoneInfo);
        return isValid ? null : {phoneRequired: true};
    };

    private setupCompromisedPaymentInstrumentValidation(): void {
        this.form.setValidators([
            this.paymInstrRequisiteValidator,
            this.creditReceiverPhoneValidator,
            this.requisiteOrganizationUnityValidator,
            this.paymentVictimRequisiteValidator,
        ]);
        this.form.updateValueAndValidity({emitEvent: false});
        this.paymInstrTypeSubscription?.unsubscribe();
        this.paymInstrTypeSubscription = this.form.root?.get('commonInformationForm.type')?.valueChanges
            ?.subscribe(() => this.form.updateValueAndValidity());
    }

    private isIncidentType(type: IncidentType): boolean {
        return (
            this.form.root?.get('commonInformationForm.type')?.value ??
            this.activatedRoute.snapshot.queryParamMap.get(INCIDENT_TYPE_CODE)
        ) === type;
    }
}

export declare type ParticipantFormValue = ɵTypedOrUntyped<ParticipantForm, ɵFormGroupRawValue<ParticipantForm>, any>;

export interface ParticipantForm {
    id?: FormControl<string>;
    subjectType?: FormControl<SubjectType>;
    notMyClient?: FormControl<boolean>;
    role?: FormControl<string>;
    name?: FormControl<string>;
    surname?: FormControl<string>;
    patronymic?: FormControl<string>;
    originDate?: FormGroup<DateForm>;
    gender?: FormControl<string>;
    iin?: FormControl<string>;
    country?: FormControl<string>;
    bin?: FormControl<string>;
    organizationName?: FormControl<string>;
    phoneNumberId?: FormControl<string>;
    phoneNumber?: FormControl<string>;
    requisiteOrganizationType?: FormControl<string>;
    accountId?: FormControl<string>;
    selectedPhoneInfo?: FormControl<PhoneInfo>;
    accountStatus?: FormControl<string>;
    accountBalance?: FormControl<number>;
    accountCurrency?: FormControl<string>;
    accountIssuerBin?: FormControl<string>;
    cardMaskNumberId?: FormControl<string>;
    cardMaskNumber?: FormControl<string>;
    iban?: FormControl<string>;
    requisiteType?: FormControl<string>;
    cardIssuerBin?: FormControl<string>;
    walletId?: FormControl<string>;
    walletNumber?: FormControl<string>;
    walletIssuerBin?: FormControl<string>;
    foreignAccountId?: FormControl<string>;
    foreignIban?: FormControl<string>;
    foreignIssuerBin?: FormControl<string>;
    paymentPointId?: FormControl<string>;
    paymentPointType?: FormControl<string>;
    paymentPointIssuerBin?: FormControl<string>;
    paymentPointNumber?: FormControl<string>;
    paymentPointAddress?: FormControl<string>;
    paymentPointStreet?: FormControl<string>;
    paymentPointHouseNumber?: FormControl<string>;
    refundBin?: FormControl<string>;
}

export function createParticipantRequest(participant: ParticipantFormValue, isTransactionDeleted = false): Participant {
    const account = participant.iban?.trim() ? {
        id: participant.accountId,
        iban: participant.iban?.trim() || null,
        issuerBin: participant.accountIssuerBin?.trim() || null,
        currency: sanitizeDigits(participant.accountCurrency),
        status: participant.accountStatus as AccountStatus,
        balance: participant.accountBalance == null || `${participant.accountBalance}` === ''
            ? null
            : Number(participant.accountBalance),
        is_deleted: isTransactionDeleted,
    } : null;
    const foreignAccount = participant.foreignIban?.trim() ? {
        id: participant.foreignAccountId,
        iban: participant.foreignIban?.trim() || null,
        issuerBin: participant.foreignIssuerBin?.trim() || null,
        isForeignAccount: true,
        is_deleted: isTransactionDeleted,
    } : null;
    return <Participant>{
        id: participant.id,
        type: participant.subjectType,
        incident_role: participant.role,
        notMyClient: participant.notMyClient ?? false,
        identification_number: participant.iin?.trim() || participant.bin?.trim() || null,
        requisiteType: participant.requisiteType as RequisiteType,
        country: sanitizeDigits(participant.country),
        account: account,
        foreignAccount: foreignAccount,
        card_mask: {
            id: participant.cardMaskNumberId,
            card_number: sanitizeCardNumber(participant.cardMaskNumber),
            issuer_bin: participant.cardIssuerBin,
            is_deleted: isTransactionDeleted,
        },
        phone_number: {
            id: participant.phoneNumberId,
            number: getValidPhoneNumberOrNull(participant.phoneNumber, participant.selectedPhoneInfo),
            is_deleted: isTransactionDeleted,
        },
        info: {
            name: participant.name || participant.organizationName,
            surname: participant.surname,
            patronymic: participant.patronymic,
            gender: participant.gender?.trim() || null,
            origin_date: getFormattedDateOrNull(participant.originDate?.date, DATE_SERVER_FORMAT),
            is_deleted: isTransactionDeleted,
        },
        wallet: {
            number: participant.walletNumber?.trim() || null,
            issuer_bin: participant.walletIssuerBin?.trim() || null,
            id: participant.walletId,
            is_deleted: isTransactionDeleted,
        },
        paymentPoint: {
            number: participant.paymentPointNumber?.trim() || null,
            address: participant.paymentPointAddress?.trim() || null,
            street: participant.paymentPointStreet?.trim() || null,
            house_number: participant.paymentPointHouseNumber?.trim() || null,
            issuer_bin: participant.paymentPointIssuerBin?.trim() || null,
            id: participant.paymentPointId,
            is_deleted: isTransactionDeleted,
        },
    };
}

export interface ParticipantViewModel extends Participant {
    refundBin?: string;
}

export function sanitizeDigits(value?: string): string | null {
    if (!value) {
        return null;
    }
    const digits = value.replace(/\D+/g, '').trim();
    return digits.length > 0 ? digits : null;
}

export function sanitizeCardNumber(value?: string): string | null {
    const sanitized = value?.replace(/\s+/g, '') ?? '';
    return /\d/.test(sanitized) ? sanitized : null;
}

export function createParticipantRequestV2(participant: ParticipantFormValue): ParticipantRequestV2 {
    const requisiteType = participant.requisiteType as RequisiteType;
    return <ParticipantRequestV2>{
        id: participant.id,
        role: participant.role?.toUpperCase(),
        subjectType: participant.subjectType,
        notMyClient: participant.notMyClient ?? false,
        identificationNumber: participant.iin?.trim() || participant.bin?.trim() || null,
        requisiteType: requisiteType || null,
        country: sanitizeDigits(participant.country),
        info: {
            name: participant.name || participant.organizationName,
            surname: participant.surname,
            patronymic: participant.patronymic,
            gender: participant.gender?.trim() || null,
            originDate: getFormattedDateOrNull(participant.originDate?.date, DATE_SERVER_FORMAT),
        },
        phoneNumber: {
            id: participant.phoneNumberId,
            number: getValidPhoneNumberOrNull(participant.phoneNumber, participant.selectedPhoneInfo),
        },
        ...createAllRequisitesV2(participant),
    };
}

function createAllRequisitesV2(participant: ParticipantFormValue): Partial<ParticipantRequestV2> {
    const requisites: Partial<ParticipantRequestV2> = {};
    const isForeignAccountRequisite = participant.requisiteType === RequisiteType.ACCOUNT_FOREIGN_BANK;
    const iban = participant.iban?.trim();
    if (iban && !isForeignAccountRequisite) {
        requisites.account = <AccountRequestV2>{
            id: participant.accountId,
            iban: iban,
            issuerBin: participant.accountIssuerBin?.trim() || null,
            currency: sanitizeDigits(participant.accountCurrency),
            status: participant.accountStatus as AccountStatus,
            balance: participant.accountBalance == null || `${participant.accountBalance}` === ''
                ? null : Number(participant.accountBalance),
        };
    } else if (participant.accountId) {
        requisites.account = <AccountRequestV2>{id: participant.accountId};
    }
    const foreignIban = participant.foreignIban?.trim();
    if (foreignIban && isForeignAccountRequisite) {
        requisites.foreignAccount = <AccountRequestV2>{
            id: participant.foreignAccountId,
            iban: foreignIban,
            issuerBin: participant.foreignIssuerBin?.trim() || null,
            isForeignAccount: true,
        };
    } else if (participant.foreignAccountId) {
        requisites.foreignAccount = <AccountRequestV2>{id: participant.foreignAccountId};
    }
    const cardNumber = sanitizeCardNumber(participant.cardMaskNumber);
    if (cardNumber || participant.cardMaskNumberId) {
        requisites.accountCard = <AccountCardRequestV2>{
            id: participant.cardMaskNumberId,
            cardNumber: cardNumber,
            issuerBin: participant.cardIssuerBin,
        };
    }
    if (participant.walletNumber?.trim() || participant.walletId) {
        requisites.wallet = <WalletRequestV2>{
            id: participant.walletId,
            number: participant.walletNumber?.trim() || null,
            issuerBin: participant.walletIssuerBin?.trim() || null,
        };
    }
    if (participant.paymentPointNumber?.trim() || participant.paymentPointAddress?.trim()
        || participant.paymentPointIssuerBin?.trim() || participant.paymentPointId) {
        requisites.paymentPoint = <PaymentPointRequestV2>{
            id: participant.paymentPointId,
            issuerBin: participant.paymentPointIssuerBin?.trim() || null,
            number: participant.paymentPointNumber?.trim() || null,
            address: participant.paymentPointAddress?.trim() || null,
            street: participant.paymentPointStreet?.trim() || null,
            houseNumber: participant.paymentPointHouseNumber?.trim() || null,
        };
    }
    return requisites;
}
